import React, { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import Step1BasicInfo from "./steps/Step1BasicInfo";
import Step2ContactDetails from "./steps/Step2ContactDetails";
import Step3Industries from "./steps/Step3Industries";
import Step4Documents from "./steps/Step4Documents";
import Step5Summary from "./steps/Step5Summary";
import { Button, message } from "antd";
import { CALL_API_UPDATED } from "../../../common/API";
import { useHistory, useLocation } from "react-router-dom";
import { useSimpleFetch } from "../../../hooks/useSimpleFetch";
import queryKeys from "../../../common/queryKey";
import { useScrollFetch } from "../../../hooks/useScrollFetch";
import {
    getIsUserLoggedIn,
    getUserInfo,
    getIsInsytsCrmManagingOffice,
    getUserName,
} from "redux/selectors";
import { connect, useSelector } from "react-redux";

const FV = "@OData.Community.Display.V1.FormattedValue";

// Capitalizes only the first letter: "yes" -> "Yes", "no" -> "No"
const capitalizeFirst = (val) => {
    if (typeof val !== "string" || val.length === 0) return val;
    const trimmed = val.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const AddClientParent = () => {
    const methods = useForm({
        mode: "onChange",
        shouldUnregister: false,
    });

    const { trigger, reset } = methods;

    const userInfos = useSelector(getUserInfo);
    const isInsytsCrm = useSelector(getIsInsytsCrmManagingOffice);

    const history = useHistory();
    const location = useLocation();

    // Fallback only (direct URL open or refresh)
    const clientListPath =
        location.state?.from ?? (isInsytsCrm ? "/ownclient" : "/client");

    // Return to the exact page we came from (/ownclient or /client)
    const goToList = () => {
        if (history.action === "PUSH" && history.length > 1) {
            history.goBack();
        } else {
            history.push(clientListPath);
        }
    };

    const clientId = window.location.pathname.split("/").pop();
    const isEditMode = Boolean(clientId && clientId !== "add-client");
    const { getClientIdCount, getownClient } = queryKeys;

    const { data: clientIdCount } = useSimpleFetch(
        getClientIdCount().key,
        getClientIdCount().url
    );

    const { data: clientDetailsPages } = useScrollFetch(
        isEditMode ? getownClient(clientId).key : null,
        isEditMode ? { endPoint: getownClient(clientId).url } : null,
        {
            enabled: isEditMode,
            getNextPageParam: () => null,
        }
    );

    // Actual API shape: { clientName: [ {...client} ], count, LastEvaluatedKey }
    const firstPage = clientDetailsPages?.pages?.[0];
    const clientDetails =
        firstPage?.clientName?.[0] ??
        firstPage?.data?.clientName?.[0] ??
        firstPage?.response?.clientName?.[0] ??
        firstPage?.response?.client ??
        null;

    useEffect(() => {
        if (!clientDetails) return;

        // formatted-value field helper
        const fv = (key) => clientDetails?.[`${key}${FV}`];
        const statecode = fv("statecode");

        reset({
            clientId: clientDetails.bdo_id,

            /* ---------------- BASIC INFO ---------------- */
            relationshipType: fv("customertypecode"),
            engagement: fv("bdo_currentyearofengagement"),
            influential: fv("bdo_influentialstarcustomer"),
            managingOffice: fv("bdo_managingoffice"),
            companyName: clientDetails.name,
            officeEmail: clientDetails.emailaddress1,
            bulkEmail: fv("donotbulkemail"),
            followEmail: fv("followemail"),
            phone: clientDetails.address1_telephone2,
            owner: fv("_ownerid_value") ?? clientDetails.createdByName,
            size: fv("bdo_size"),
            websiteURL: clientDetails.websiteurl,
            incorporationType: fv("bdo_incorporationtype"),
            primarySector: fv("_bdo_sector_value"),
            primaryIndustry: fv("_bdo_primaryindustry_value"),
            primaryContactEmail: clientDetails.emailaddress2,
            primaryContactPerson: fv("_primarycontactid_value"),
            description: clientDetails.description,

            /* ---------------- ADDRESS ---------------- */
            addressType: clientDetails.bdo_addresstype,
            registeredAddress: clientDetails.bdo_registeredaddress,
            country: clientDetails.country,
            registeredNo: clientDetails.bdo_registrationno,
            registeredOfficePostalAddress: clientDetails.address1_line1,
            useGroupAddress: clientDetails.bdo_usegroupaddress,
            city: clientDetails.address1_city,
            operatingCountries: clientDetails.bdo_operatingcountries,
            pinNo: clientDetails.bdo_pin,
            postalCode: clientDetails.bdo_postalcode,

            /* ---------------- RELATIONSHIP ---------------- */
            liaisonManager: fv("_bdo_relationship_value"),
            liaisonManagerRole: fv("bdo_relationshipliaisonmanagersrole"),
            aliasName: clientDetails.bdo_othersideentitynamealiasname,
            classificationType: fv("bdo_classificationtype"),
            secretory: clientDetails.bdo_secretory,
            liaisonEmail:
                clientDetails.relationshipliaisonmanagererelationshipliaisonmanageremailmai,
            liaisonId: clientDetails.bdo_relationshipliaisonmanagerid,
            entityType: fv("bdo_entitytype"),
            principleLawyer: clientDetails.bdo_principlelawyer,
            principleBanker: clientDetails.bdo_principlebanker,

            /* ---------------- OTHER INFORMATION ---------------- */
            bcClientId: clientDetails.bdo_bcclientid,
            riskClassification: fv("bdo_riskclassification"),
            reviewCyclePeriod: fv("bdo_reviewcycleperiod"),
            status: statecode ? (statecode === "Active" ? "Active" : "Inactive") : "",
            inactiveReason: clientDetails.inactivereason ?? "",
            sizeMarketing: clientDetails.otherInfoSize,
            domicile: fv("_bdo_domicile_value"),
            typeOfPIE: clientDetails.bdo_typeofpie,
            reviewDueDate: clientDetails.bdo_reviewduedate,
            reviewCountryIncorp: fv("_bdo_domicile_value"),
            publicInterestEntity: fv("bdo_publicinterestentity"),
            regulator: fv("bdo_regulator"),

            /* ---------------- MARKETING ---------------- */
            lastDateCampaign: clientDetails.lastusedincampaign,
            sendMarketingMaterials: fv("donotsendmm"),

            /* ---------------- BUSINESS RELATIONSHIP ---------------- */
            businessRelationship: fv("bdo_businessrelationship"),
            brFlagValue: fv("bdo_br_flag_value"),
            designationTemplate: fv("bdo_designationtemplate"),
            createdDate: clientDetails.bdo_createddate,
            archiveReason: clientDetails.bdo_removalarchivereason,
            strategicPartner: fv("bdo_strategicpartner"),
            businessDesignationType: fv("bdo_businessdesignationtype"),
            designationLevel: fv("bdo_designationlevellocation_hierarchy"),
            archiveDate: clientDetails.bdo_removalarchivedate,
            reference: clientDetails.bdo_referenceoptional,

            /* -------- Eligibility & Independence -------- */
            eligibleFirm: fv("bdo_thefirmisqualifiedandiseligibleinca"),
            complianceRules: fv("bdo_firmsrulesandproceduresasdescribedintheb"),

            /* -------- Prospective Client's Reputation -------- */
            referredDiscussion: fv("bdo_discusswithreferrersasdescribedintheb"),
            financialReview: fv("bdo_theprospectiveclientsfs"),
            referredDiscussionAgain: fv("bdo_theprospectiveclientwithpartnersandmanage"),
            financialReviewAgain: clientDetails.bdo_iftheaboveproceduresareinconclusivecontac,

            /* -------- International Referrals -------- */
            discussedWithParentPartner: fv("bdo_prospectiveclientsreputation"),
            discussedWithInternationalPartner: fv("bdo_internationallaisonpartner"),
            otherFirmProcedures: fv("bdo_othermemberfirmsacceptanceprocedurescon"),

            /* -------- Previous Auditors -------- */
            authorizePreviousAuditors: fv("bdo_theprospectiveclienttoauthorizethepreviou"),
            previousAuditorsDecision: fv("bdo_thepreviousauditortodeterminifthereisanyreason"),
            contactPreviousAuditor: fv("bdo_contactwithpreviousauditortodiscussmatte"),
            changingAuditorsReason: fv("bdo_heprospectiveclient"),
            accessAuditorWorkingPapers: fv("bdo_thepreviousauditorsworkingpapersasrelevan"),

            /* -------- Ability to Serve -------- */
            staffAvailability: fv("bdo_appropriatepartnersandstaffareavailableto"),
            specialistExpertise: fv("bdo_firmhasorcanobtainanyspecialistexpertise"),

            /* -------- Fees -------- */
            feeBasis: fv("bdo_theagreedprincipalbasisonwhichthefeesshal"),
            independentFeeArrangement: fv("bdo_firmindependentwithrespecttotheclientrega"),
            proBonoRationale: fv("bdo_incaseofaprobonoengagement"),

            /* -------- Industry Background & Risk -------- */
            industryBackground: fv("bdo_clientstypeofbusinessandbriefdetailsabout"),
            negativeConcernsMarket: fv("bdo_arethereanynegativeconcernsabouttheprospe"),
            publiclyHeld: fv("bdo_istheprospectiveclientpubliclyheldorexpec"),
            listedOnStockExchange: fv("bdo_listedonastockexchange"),
            managementAttitude: fv("bdo_managementsattitudetocontrolsystemsitspla"),
            accountingCompetence: fv("bdo_theapparentcompetenceofaccountingfinancia"),
            crimeConviction: fv("bdo_hastheprospectiveclientoranyofitsdirector"),
            abilityContinue: fv("bdo_istheresubstantialdoubtconcerningtheprosp"),
            continuityManagement: fv("bdo_recentcontinuityofmanagementanditsperceiv"),
            financialNeedsCompany: fv("bdo_thefinancialneedsofthecompanyanditsabilit"),
            growthSource: fv("bdo_therapidityofgrowthinrecentyearsandtheso"),
            characterIntegrity: fv("bdo_hastheprospectiveclientscharacterintegrit"),

            /* -------- Independence Checks -------- */
            noLitigation: fv("bdo_arewesatisfiedthatthereisnolitigationtaki"),
            noFamilyConnections: fv("bdo_canweconfirmthatnopartnersorstaffengagedi"),
            confirmPartnerConnections: fv("bdo_canweconfirmthatanypartnerorcloselyconnec"),
            noScopeLimitations: fv("bdo_arewesatisfiedthattherearenolimitationsof"),
            clientBusinessDetails: fv("bdo_clientofbusiness"),
            noOtherImpairments: fv("bdo_arewesatisfiedthattherearenootherfactorsw"),
            noOtherFactorsPeriod: fv("bdo_havewesatisfiedourselvesthattherewerenoot"),

            /* -------- Risk Rating -------- */
            anticipatingPublicOffering: fv("bdo_anticipatingapublicofferingwithinthenextt"),
            buyoutOrTakeover: fv("bdo_couldbesubjectofabuyoutortakeoverwithint"),
            highTechOrRegulatedIndustry: fv("bdo_involvedinhightechemergingorheavilyhighly"),
            aggressiveAccountingPolicies: fv("bdo_aggressiveaccountingpoliciesundueemphasis"),
            complexAccountingPrinciples: fv("bdo_complexaccountingprinciples"),
            continuingLosses: fv("bdo_continuinglossesandorexperiencingliquidit"),
            unrealisticReportingDeadlines: fv("bdo_unrealisticreportingdeadlines"),
            poorDebtPosition: fv("bdo_poordebtposition"),
            dominatingManagement: fv("bdo_domineeringchiefexecutivemanagement"),
            weakInternalControls: fv("bdo_weakinternalcontrolsandpoormanagement"),
            offshoreTaxHavens: fv("bdo_engagesinoffshoretransactionsintaxhavenso"),
            foreignSubsidiaries: fv("bdo_hasforeignsubsidiariesaffiliates"),
            reputationSullied: fv("bdo_clientmanagementreputationsullied"),
            predecessorAuditIssues: fv("bdo_disputesunresolvedissueswithpredecessorau"),
            evasiveOrAbusiveBehavior: fv("bdo_isevasiveorabusivetobdoeastafricastaffdur"),

            /* -------- Conclusion -------- */
            liaisonConclusion: fv("_bdo_relationship_value"),
            signature: clientDetails.conclusionSignature,
            conclusionDate: clientDetails.conclusionDate,
            localCOI: fv("bdo_hasthelocalcoibeendone"),
            globalCheck: fv("bdo_hastheglobalcheckbeendone"),
            amlWorldCheck: fv("bdo_hasamlandworldcheckbeendone"),
            corporateTreeRequest: fv("bdo_hasthecorporatetreebeenrequested"),

            /* -------- Conclusion Approval -------- */
            liaisonManagerApproval: fv("_bdo_relationship_value"),
            signatureApproval: clientDetails.conclusionSignature,
            approvalDate: fv("bdo_date"),

            /* -------- Final Acceptance (first letter capitalized) -------- */
            acceptanceCompleted: capitalizeFirst(fv("bdo_coicheckpassed")),
            clientOnboarded: capitalizeFirst(fv("bdo_onboarded")),

            /* -------- Login credentials -------- */
            loginOfficeEmail: clientDetails.login_officeEmail,
            loginPassword: clientDetails.login_password,
        });
    }, [clientDetails, reset]);

    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    const next = async () => {
        let fields = [];

        if (step === 1) {
            fields = [
                "relationshipType",
                "engagement",
                "influential",
                "managingOffice",
                "primaryContactEmail",
                "primaryContactPerson",
            ];
        }

        if (step === 2) {
            fields = [
                // Other Information
                "bcClientId",
                "riskClassification",
                "reviewCyclePeriod",
                "status",
                "inactiveReason",

                // Marketing
                "lastDateCampaign",

                // Business Relationship
                "businessRelationship",
                "brFlagValue",
                "designationTemplate",
                "createdDate",
                "archiveReason",
                "strategicPartner",
                "businessDesignationType",
                "designationLevel",
                "archiveDate",
            ];
        }

        // STEP 5 (Login Credentials)
        if (step === 5) {
            fields = ["loginOfficeEmail", "loginPassword"];
        }

        const isValid = await trigger(fields, { shouldFocus: true });

        if (isValid) {
            setStep((prev) => prev + 1);
        }
    };

    const prev = () => setStep((prev) => prev - 1);

    const onSubmit = async (data) => {
        const payload = {
            clientId: data.clientId,
            influentialstarCustomer: data?.influential,
            relationshipType: data?.relationshipType,
            currentNatureofEngag: data.engagement || "Ongoing",
            companyRegisteredName: data.companyName,
            owner: data.owner,
            size: data.size,
            countryofIncorporation: data.reviewCountryIncorp,
            managingOffice: data.managingOffice,
            emailaddress: data.officeEmail,
            bulkEmail: data.bulkEmail,
            followEmail: data.followEmail,
            phone: data.phone,
            incorporationType: data.incorporationType,
            companyWebsiteUrl: data.websiteURL,
            primaryIndustry: data.primaryIndustry,
            primarySector: data?.primarySector,
            primaryContactPerson: data.primaryContactPerson,
            primaryContactEmail: data.primaryContactEmail,
            description: data.description,

            //Address
            bdoAddresstype: data.addressType,
            bdoUsegroupaddress: data.useGroupAddress,
            bdoRegisteredaddress: data.registeredAddress,
            address1City: data.city,
            country: data.country,
            bdoOperatingcountries: data.operatingCountries,
            bdoRegistrationno: data.registeredNo,
            bdoPin: data.pinNo,
            officepostalAddress: data.registeredOfficePostalAddress,
            bdoPostalcode: data.postalCode,

            //Relationship
            relationshipManagerEmail: data.liaisonEmail,
            relationshipManagerRole: data.liaisonManagerRole,
            relationshipLiaisonManagerId: data.liaisonId,
            otherSideEntityAliasName: data.aliasName,
            entityType: data.entityType,
            classificationType: data.classificationType,
            principleLawyer: data.principleLawyer,
            secretory: data.secretory,
            principleBanker: data.principleBanker,

            //other information
            bcClientId: data.bcClientId,
            riskClassification: data.riskClassification,
            reviewCyclePeriod: data.reviewCyclePeriod,
            reviewDueDate: data.reviewDueDate,
            status: data.status,
            // Only sent when the client is Inactive
            inactivereason:
                data.status === "Inactive" ? (data.inactiveReason || "").trim() : "",
            otherInfoSize: data?.sizeMarketing,
            domicile: data.domicile,
            publicInterestEntity: data.publicInterestEntity === "Yes" ? true : false,
            typeOfPie: data.typeOfPIE,
            regulator: data.regulator,

            //marketing
            lastusedincampaign: data?.lastDateCampaign,
            sendMarketingMaterials: data?.sendMarketingMaterials,

            //Business Relationship
            businessRelationship: data.businessRelationship,
            strategicPartner: data.strategicPartner,
            brFlagValue: data.brFlagValue,
            businessDesignationType: data.businessDesignationType,
            designationTemplate: data.designationTemplate,
            designationLevelLocationHierarchy: data.designationLevel,
            createdDate: data.createdDate,
            removalArchiveDate: data.archiveDate,
            removalArchiveReason: data.archiveReason,
            reference: data?.reference,

            //Eligibility & Independence
            ensureFirmQualified: data.eligibleFirm,
            ensureFirmCompliesWithRules: data.complianceRules,

            //The Prospective Client's Reputation
            discussWithReferrer: data.referredDiscussion,
            reviewProspectiveClientFinancials: data.financialReview,
            discussWithPartnersManagers: data?.referredDiscussionAgain,
            inconclusiveContactFriends: data?.financialReviewAgain,

            //in case of internal referral
            parentEnagaementPartner: data?.discussedWithParentPartner,
            discussedWithInternationalPartner: data?.discussedWithInternationalPartner,
            memberFirmAcceptanceProcedures: data.otherFirmProcedures,

            //previous auditor
            authorizePreviousAuditor: data.authorizePreviousAuditors,
            previousAuditorsDecision: data.previousAuditorsDecision,
            contactPreviousAuditor: data.contactPreviousAuditor,
            prospectiveClientCompanyChangingAuditors: data?.changingAuditorsReason,
            previousAuditorsWorkingPapers: data?.accessAuditorWorkingPapers,

            //Ability to serve the client
            appropriatePartnersAvailable: data?.staffAvailability,
            specialistExpertise: data?.specialistExpertise,
            feeBasis: data?.feeBasis,
            firmIndependent: data?.independentFeeArrangement,
            proBonoRationale: data?.proBonoRationale,

            //Investigation of industry background & clients
            clientsTypeOfBusiness: data.industryBackground,
            negativeConcerns: data.negativeConcernsMarket,
            publiclyHeld: data?.publiclyHeld,
            managementAttitude: data.managementAttitude,
            competenceOfPersonnel: data.accountingCompetence,
            convictedOfCrime: data?.crimeConviction,
            doubtOfExistence: data?.abilityContinue,
            continuityOfManagement: data.continuityManagement,
            financialNeeds: data?.financialNeedsCompany,
            rapidGrowth: data?.growthSource,
            characterIntegrity: data.characterIntegrity,

            //litigation
            noLitigation: data?.noLitigation,
            noFamilyConnections: data?.noFamilyConnections,
            confirmPartnerConnections: data?.confirmPartnerConnections,
            noScopeLimitations: data?.noScopeLimitations,
            clientBusinessDetails: data?.clientBusinessDetails,
            noOtherImpairments: data?.noOtherImpairments,
            noOtherFactorsPeriod: data?.noOtherFactorsPeriod,

            //risk
            listedOnStockExchange: data?.listedOnStockExchange,
            anticipatingPublicOffering: data?.anticipatingPublicOffering,
            subjectToBuyoutOrTakeover: data?.buyoutOrTakeover,
            involvedInHighTech: data?.highTechOrRegulatedIndustry,
            otherPublicInterestEntity: data?.publicInterestEntity,
            aggressiveAccountingPolicies: data?.aggressiveAccountingPolicies,
            complexAccountingPrinciples: data?.complexAccountingPrinciples,
            continuingLosses: data?.continuingLosses,
            unrealisticReportingDeadlines: data?.unrealisticReportingDeadlines,
            poorDebtPosition: data?.poorDebtPosition,
            domineeringManagement: data?.dominatingManagement,
            weakInternalControls: data?.weakInternalControls,
            offshoreTransactions: data?.offshoreTaxHavens,
            foreignSubsidiaries: data?.foreignSubsidiaries,
            managementReputationSullied: data?.reputationSullied,
            disputesWithPredecessorAuditor: data?.predecessorAuditIssues,
            evasiveOrAbusive: data?.evasiveOrAbusiveBehavior,

            //conclusion
            relationshipManager: data.liaisonManager,
            acceptanceSignature: data.signatureApproval,
            acceptanceDate: data.approvalDate,

            //COI CHECK PROCESS
            localCOI: data.localCOI,
            globalCheck: data.globalCheck,
            worldCheck: data.amlWorldCheck,
            corporateTree: data.corporateTreeRequest,

            //Conclusion: I have performed
            conclusionRelationship: data?.liaisonManagerApproval,
            conclusionSignature: data?.signatureApproval,
            conclusionDate: data.conclusionDate,
            clientOnboarded: data?.clientOnboarded,
            clientAcceptanceCompleted: data?.acceptanceCompleted,

            //credentials
            loginOfficeEmail: data?.loginOfficeEmail,
            loginPassword: data?.loginPassword,
            createdByMailId: userInfos?.userid,
            createdByName: userInfos?.name,
            createdByJobTitle: userInfos?.job_title,
        };

        let response;

        setSubmitting(true);
        try {
            if (isEditMode) {
                response = await CALL_API_UPDATED(
                    `insyts-crm-clients/BDO/${data.clientId}`,
                    "patch",
                    payload
                );
            } else {
                response = await CALL_API_UPDATED(`insyts-crm-clients`, "post", payload);
            }

            if (response?.status === "SUCCESS") {
                message.success(
                    isEditMode ? "Client updated successfully" : "Client added successfully"
                );
                goToList();
            } else if (response?.code && response?.message) {
                // API resolved normally but returned an error body like
                // { code: 400, message: "..." }
                message.error(response.message);
            } else {
                message.error(response?.response?.message || "Failed to save client.");
            }
        } catch (error) {
            // Covers CALL_API_UPDATED throwing on non-2xx responses
            const apiMessage =
                error?.response?.data?.message ||
                error?.data?.message ||
                error?.message;
            message.error(apiMessage || "Failed to save client.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ background: "#fff", padding: 30, minHeight: "100vh" }}>
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    {/* Breadcrumb & Stepper */}
                    <div style={{ marginBottom: 30 }}>
                        <div style={{ fontSize: 14, color: "#6c757d", marginBottom: 15 }}>
                            <span onClick={goToList} style={{ cursor: "pointer" }}>
                                Client
                            </span>{" "}
                            &nbsp; &gt; &nbsp;
                            <span style={{ fontWeight: 600 }}>
                                {isEditMode
                                    ? `Editing Client - ${clientDetails?.name ?? ""}`
                                    : "Adding New Client"}
                            </span>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 40,
                            }}
                        >
                            {[1, 2, 3, 4].map((item) => (
                                <div key={item} style={{ display: "flex", alignItems: "center" }}>
                                    <div
                                        style={{
                                            width: 55,
                                            height: 55,
                                            borderRadius: "50%",
                                            border: "1.5px solid #B8B8B8",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: step === item ? "#E6FAF3" : "#FFFFFF",
                                            color: step === item ? "#000" : "#9AA0A6",
                                            fontWeight: 600,
                                            fontSize: 20,
                                        }}
                                    >
                                        {item}
                                    </div>

                                    {item !== 4 && (
                                        <div
                                            style={{
                                                width: 80,
                                                height: 2,
                                                background: "#DDD",
                                                margin: "0 10px",
                                            }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* STEP COMPONENTS */}
                    {step === 1 && (
                        <Step1BasicInfo
                            count={clientIdCount?.response?.data?.count}
                            isEditMode={isEditMode}
                        />
                    )}
                    {step === 2 && <Step2ContactDetails isEditMode={isEditMode} />}
                    {step === 3 && <Step3Industries />}
                    {step === 4 && <Step4Documents />}
                    {/* {step === 5 && <Step5Summary />} */}

                    {/* Navigation Buttons */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginTop: 30,
                            gap: 12,
                        }}
                    >
                        <Button
                            type="text"
                            onClick={goToList}
                            style={{
                                fontSize: 16,
                                fontWeight: 500,
                                color: "#000",
                                padding: "6px 22px",
                            }}
                        >
                            Cancel
                        </Button>

                        {step > 1 && (
                            <Button
                                onClick={prev}
                                style={{
                                    borderColor: "#000",
                                    color: "#000",
                                    fontWeight: 500,
                                    padding: "6px 22px",
                                    borderRadius: 6,
                                }}
                            >
                                Previous
                            </Button>
                        )}

                        {step < 4 && (
                            <Button
                                type="primary"
                                onClick={next}
                                style={{
                                    background: "#B5F3DA",
                                    color: "#000",
                                    fontWeight: 600,
                                    borderRadius: 6,
                                    padding: "6px 22px",
                                    border: "none",
                                }}
                            >
                                Next
                            </Button>
                        )}

                        {step === 4 && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitting}
                                disabled={submitting}
                                style={{
                                    background: "#B5F3DA",
                                    color: "#000",
                                    fontWeight: 600,
                                    borderRadius: 6,
                                    padding: "6px 22px",
                                    border: "none",
                                }}
                            >
                                {isEditMode ? "Update" : "Complete"}
                            </Button>
                        )}
                    </div>
                </form>
            </FormProvider>
        </div>
    );
};

const mapStateToProps = (state) => ({
    isUserLoggedIn: getIsUserLoggedIn(state),
    userName: getUserName(state),
    userInfo: getUserInfo(state),
});

export default connect(mapStateToProps)(AddClientParent);